import React, { useState } from 'react';
import { SPACE, RADII, FONT_SIZE, FONT_WEIGHT, COLORS, DURATION } from '../theme';

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  badge?: string;
  children: React.ReactNode;
}

// Exact-fidelity literals carried over from the prior styled
// definition — keeping these makes the de-style pass a visual no-op.
const ACCENT = '#fbbf24';
const CLOSED_COLOR = '#888';
const HOVER_COLOR = '#ddd';
const BADGE_BG = 'rgba(251, 191, 36, 0.15)';

function headerStyle(open: boolean, hovered: boolean): React.CSSProperties {
  const color = hovered ? HOVER_COLOR : open ? ACCENT : CLOSED_COLOR;
  return {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE.sm,
    padding: `${SPACE.sm}px 0`,
    cursor: 'pointer',
    fontSize: FONT_SIZE.sm,
    fontWeight: FONT_WEIGHT.bold,
    color,
    textTransform: 'uppercase',
    borderBottom: `1px solid ${COLORS.borderFaint}`,
    userSelect: 'none',
    transition: `color ${DURATION.fast}`,
  };
}

function arrowStyle(open: boolean): React.CSSProperties {
  return {
    fontSize: 9,
    transition: `transform ${DURATION.fast}`,
    transform: `rotate(${open ? '90deg' : '0deg'})`,
  };
}

const badgeStyle: React.CSSProperties = {
  fontSize: 9,
  padding: `1px ${SPACE.xs + 1}px`,
  background: BADGE_BG,
  borderRadius: RADII.sm,
  color: ACCENT,
  fontWeight: FONT_WEIGHT.normal,
  textTransform: 'none',
};

const contentStyle: React.CSSProperties = {
  padding: `${SPACE.md}px 0 ${SPACE.xs}px 0`,
};

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title, defaultOpen = false, badge, children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [hovered, setHovered] = useState(false);

  return (
    <div>
      <div
        style={headerStyle(open, hovered)}
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span style={arrowStyle(open)}>&#9654;</span>
        {title}
        {badge && <span style={badgeStyle}>{badge}</span>}
      </div>
      {open && <div style={contentStyle}>{children}</div>}
    </div>
  );
};
